import React, { useState, useRef } from 'react';
import '../styles/youtubeEmbed.css';
import '../styles/driveVideoEmbed.css';

/**
 * DriveVideoEmbed — plays a Google Drive video via the drive-stream-proxy
 * Edge Function. Public API mirrors YouTubeEmbed so callers can route by
 * URL type without changing layout:
 *
 *   <YouTubeEmbed videoId={ytId}  format="landscape" />
 *   <DriveVideoEmbed streamUrl={...} format="landscape" />
 *
 * Why a proxy rather than a direct Drive URL:
 *   The drive-stream-proxy Edge Function adds caching, range-request
 *   support, and bypasses Google's CORP: same-site restrictions that
 *   would otherwise block <video> playback from a third-party domain.
 *   The proxy URL is built once via utils/driveStreamUrl.js — keeping
 *   that the single source of truth means swapping the proxy implementation
 *   later requires a one-file change.
 *
 * Why HTML5 <video> rather than the `<iframe ...drive.google.com/.../preview>` route:
 *   - No Google branding bar across the playback surface
 *   - Native fullscreen, AirPlay, PiP all work
 *   - Mobile data-saver / autoplay-with-mute interactions are predictable
 *   - The video element style matches what the YouTube iframe shows
 */
const DriveVideoEmbed = ({ streamUrl, format = 'landscape', poster, autoPlay = false }) => {
  const videoRef = useRef(null);
  const [errored, setErrored] = useState(false);

  if (!streamUrl) {
    return (
      <div className="youtube-error">
        <p>No video stream URL provided.</p>
      </div>
    );
  }

  if (errored) {
    return (
      <div className="youtube-error">
        <p>This Drive video couldn't be played. It may be restricted or no longer shared.</p>
        <p style={{ marginTop: 8 }}>
          <a href={streamUrl} target="_blank" rel="noopener noreferrer">Open in new tab</a>
        </p>
      </div>
    );
  }

  // We reuse YouTubeEmbed's CSS classes (.youtube-container, .youtube-landscape,
  // .youtube-portrait, .youtube-square) so the embed has identical sizing,
  // aspect ratios, and responsive behaviour. The driveVideoEmbed.css file
  // only adds rules for the inner <video> element so it fills the wrapper.
  if (format === 'portrait') {
    return (
      <div className="youtube-container">
        <div className="youtube-portrait-container">
          <div className="youtube-portrait">
            <video
              ref={videoRef}
              className="drive-video-embed"
              src={streamUrl}
              poster={poster}
              controls
              playsInline
              preload="metadata"
              autoPlay={autoPlay}
              onError={() => setErrored(true)}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="youtube-container">
      <div className={`youtube-${format}`} data-testid={`drive-container-${format}`}>
        <video
          ref={videoRef}
          className="drive-video-embed"
          src={streamUrl}
          poster={poster}
          controls
          playsInline
          preload="metadata"
          autoPlay={autoPlay}
          onError={() => setErrored(true)}
        />
      </div>
    </div>
  );
};

export default DriveVideoEmbed;
